import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, formatDisplayAmount, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, Plus, PackageCheck, CircleDollarSign, XCircle, Trash2 } from "lucide-react";

const STATUS = {
  pending_approval: { label: "Aguarda Aprovação", cls: "bg-blue-500/20 text-blue-600" },
  ordered: { label: "Aprovada / Encomendada", cls: "bg-amber-500/20 text-amber-600" },
  received: { label: "Recebida", cls: "bg-emerald-500/20 text-emerald-600" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/20 text-red-600" },
  rejected: { label: "Rejeitada", cls: "bg-red-500/20 text-red-600" },
};

export default function PurchasesPage() {
  const { token, isAdmin } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [pRes, sRes, prodRes] = await Promise.all([
        axios.get(`${API_URL}/purchases`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/suppliers`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
      ]);
      setPurchases(pRes.data);
      setSuppliers(sRes.data);
      setProducts(prodRes.data);
    } catch { toast.error("Erro ao carregar compras"); }
    finally { setLoading(false); }
  };

  const addItem = () => setItems([...items, { product_id: "", description: "", quantity: 1, unit_cost: "" }]);
  const updateItem = (i, field, value) => {
    const next = [...items];
    next[i][field] = value;
    if (field === "product_id") {
      const p = products.find((x) => x.product_id === value);
      if (p) next[i].description = p.name;
    }
    setItems(next);
  };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totalDisplay = items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0), 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!supplierId) return toast.error("Selecione o fornecedor");
    if (items.length === 0) return toast.error("Adicione pelo menos um item");
    try {
      await axios.post(`${API_URL}/purchases`, {
        supplier_id: supplierId,
        notes,
        items: items.map((it) => ({
          product_id: it.product_id || null,
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit_cost: toBaseCurrency(it.unit_cost),
        })),
      }, { headers, withCredentials: true });
      toast.success("Ordem de compra criada");
      setModal(false); setItems([]); setSupplierId(""); setNotes("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar compra"); }
  };

  const doAction = async (purchase, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const res = await axios.put(`${API_URL}/purchases/${purchase.purchase_id}/${action}`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na operação"); }
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Compras</h1>
          <p className="text-muted-foreground">Ordens de compra — receber mercadoria atualiza o stock</p>
        </div>
        <Button onClick={() => { setItems([{ product_id: "", description: "", quantity: 1, unit_cost: "" }]); setModal(true); }} data-testid="add-purchase-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova Compra
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : purchases.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nenhuma compra registada
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {purchases.map((p) => (
            <Card key={p.purchase_id} data-testid={`purchase-card-${p.purchase_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold">{p.number}</p>
                      <Badge className={STATUS[p.status]?.cls}>{STATUS[p.status]?.label}</Badge>
                      <Badge variant="outline" className={p.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}>
                        {p.payment_status === "paid" ? "Paga" : "Por pagar"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{p.supplier_name} · {formatDate(p.created_at)} · {p.items.length} item(s)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold text-lg">{formatCurrency(p.total)}</p>
                    {p.status === "pending_approval" && isAdmin && (
                      <>
                        <Button size="sm" onClick={() => doAction(p, "approve", `Aprovar a compra ${p.number}?`)} data-testid={`approve-purchase-${p.purchase_id}`}>
                          <PackageCheck className="w-4 h-4 mr-1" /> Aprovar
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => doAction(p, "reject", `Rejeitar a compra ${p.number}?`)} data-testid={`reject-purchase-${p.purchase_id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                        </Button>
                      </>
                    )}
                    {p.status === "pending_approval" && !isAdmin && (
                      <Badge variant="outline" className="text-blue-600">Aguarda aprovação do administrador</Badge>
                    )}
                    {p.status === "ordered" && (
                      <>
                        <Button size="sm" onClick={() => doAction(p, "receive", `Receber mercadoria da compra ${p.number}? O stock será atualizado.`)} data-testid={`receive-purchase-${p.purchase_id}`}>
                          <PackageCheck className="w-4 h-4 mr-1" /> Receber
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => doAction(p, "cancel", "Cancelar esta compra?")}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {p.payment_status !== "paid" && !["cancelled", "rejected", "pending_approval"].includes(p.status) && (
                      <Button size="sm" variant="outline" onClick={() => doAction(p, "pay", `Marcar compra ${p.number} como paga?`)} data-testid={`pay-purchase-${p.purchase_id}`}>
                        <CircleDollarSign className="w-4 h-4 mr-1" /> Pagar
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {p.items.map((it, i) => (
                    <span key={i} className="mr-3">{it.quantity}x {it.description} ({formatCurrency(it.unit_cost)})</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nova Ordem de Compra</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1">
              <Label>Fornecedor *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="purchase-supplier-select"><SelectValue placeholder="Selecionar fornecedor" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {suppliers.length === 0 && <p className="text-xs text-amber-600">Crie primeiro um fornecedor na página Fornecedores</p>}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Itens</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Item</Button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Select value={it.product_id} onValueChange={(v) => updateItem(i, "product_id", v)}>
                      <SelectTrigger className="text-xs"><SelectValue placeholder="Produto (opcional)" /></SelectTrigger>
                      <SelectContent>
                        {products.map((pr) => <SelectItem key={pr.product_id} value={pr.product_id}>{pr.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input placeholder="Descrição" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} required />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" min="0.01" placeholder="Qtd" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} required />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" min="0" placeholder="Custo unit." value={it.unit_cost} onChange={(e) => updateItem(i, "unit_cost", e.target.value)} required />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" size="icon" variant="ghost" className="text-destructive" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              <p className="text-right font-bold">Total: {formatDisplayAmount(totalDisplay)}</p>
            </div>

            <div className="space-y-1">
              <Label>Notas</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-purchase-btn">Criar Compra</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
